import React, { useEffect, useMemo, useRef, useState } from 'react';
import RMSvgIcon from 'components/customComponents/CommonComponents/RMSvgIcon';
import Explorer from 'components/explorer';
import { InputWithIcon } from 'components/input';
import { treeNodeTypeToLayerTypeMap } from 'components/plateviewer/experiment/canvasDragAndDrop/constants';
import {
  addLayerDetails,
  isLayerDragDropAllowedType,
} from 'components/plateviewer/experiment/canvasDragAndDrop/utils';
import { useUpdateLayerStates } from 'components/plateviewer/experiment/hooks';
import { addNewLayer } from 'components/plateviewer/experiment/utils';
import ETreeNodeTypes from 'enums/ETreeNodeTypes';
import { FmlxBanner } from 'fmlx-common-ui';
import { useRMSelector } from 'redux/hooks';
import { getDropLayerTarget } from 'utils/ExperimentUtils';
import { expandNodesRecursively } from './utils';
import { useLayersContainerContext, useSetSelectedNodeEffect } from './hooks';
import { useIsAddLayerDisabled } from 'components/plateviewer/experiment/canvasDragAndDrop/hooks';
import { useModalHandlers } from 'components/modal';
import { ScreenToCustomlayerWarning } from 'components/plateviewer/experiment/canvasDragAndDrop';
import { useAllWellNavigationDetails } from 'redux/hooks/experiment';
interface LayersExplorerProps {
  isSelected: boolean;
}

export const LayersExplorer = (props: LayersExplorerProps) => {
  const { isSelected } = props;
  const { treeControlState, setTreeControlState } = useLayersContainerContext();
  const explorerRef = useRef(null);
  const [searchText, setSearchText] = useState('');
  const [bannerVisible, setBannerVisible] = useState(true);
  const isScreenRoute = window.location.pathname.includes('/screen');
  const updateLayerStates = useUpdateLayerStates();
  const exp = useRMSelector((state) => state.experiment.expData);
  const isAddLayerDisabled = useIsAddLayerDisabled(exp);
  const { showModal, hideModal } = useModalHandlers();
  const wellDetails = useAllWellNavigationDetails();

  useSetSelectedNodeEffect({
    treeControlState,
    setTreeControlState,
    treeNodeId: exp.treeNodeId,
  });

  useEffect(() => {
    setTreeControlState((prev) => ({
      ...prev,
      treenodes: expandNodesRecursively(prev.treenodes, searchText.trim() ? true : false),
    }));
  }, [searchText]);

  const filteredTree = useMemo(() => {
    const text = searchText.trim().toLowerCase();

    const filterNodes = (node) => {
      let filteredChildren = undefined;

      if (node.children?.length) {
        filteredChildren = node.children.map(filterNodes).filter(Boolean);
      }

      const matches =
        !text || node.name.toLowerCase().includes(text) || filteredChildren?.length > 0;

      if (!matches) {
        return null;
      }

      return {
        ...node,
        children: filteredChildren,
      };
    };

    return filterNodes;
  }, [searchText]);

  const doubleClickHandler = async (node) => {
    const { type, id, extra } = node;
    if (isAddLayerDisabled || !isLayerDragDropAllowedType(type)) {
      return;
    }
    const addLayerOnCanvas = async () => {
      hideModal();
      const selectedPlate = exp.plateDto.find(
        (p) => p.plateNumber === wellDetails?.at(0)?.plateNum,
      );
      const layerStartX = selectedPlate
        ? (selectedPlate.colPos - 1) * selectedPlate.cols
        : 0;
      const layerStartY = selectedPlate
        ? (selectedPlate.rowPos - 1) * selectedPlate.rows
        : 0;

      let layer = addNewLayer(
        layerStartX,
        layerStartY,
        exp.containerCols - 1,
        exp.containerRows - 1,
        treeNodeTypeToLayerTypeMap[node.type],
        exp,
        exp.experimentId,
      );
      let dropNumber = getDropLayerTarget(
        layer,
        exp.layers,
        exp?.containerDrops?.length,
        layer.type,
      );
      if (isScreenRoute || type === ETreeNodeTypes.Screen) {
        dropNumber = null;
      }
      layer = await addLayerDetails(
        type,
        dropNumber,
        layer,
        exp,
        id,
        node,
        extra,
        isScreenRoute,
        true, // fromLayersExplorer
      );
      updateLayerStates(layer, true);
    };

    if (isScreenRoute && type === ETreeNodeTypes.Screen) {
      showModal({
        heading: 'Warning',
        body: <ScreenToCustomlayerWarning />,
        onOk: addLayerOnCanvas,
        onCancel: hideModal,
        okButtonText: 'Yes',
        noButtonText: 'No',
        id: 'customWidthModal',
      });
    } else {
      addLayerOnCanvas();
    }
  };

  return (
    <div className="flex flex-col h-full p-2 m-2 gap-2 layers-explorer">
      <InputWithIcon
        key={'inputIcon'}
        icon={<RMSvgIcon name="Search" height="1.25rem" width="1.25rem" />}
        onChange={(e) => setSearchText(e.target.value)}
        value={searchText}
        placeholder="Search"
        inputStyle={{ height: '2.5rem' }}
        iconPosition="left"
      />
      <FmlxBanner
        variant="info"
        title="Drag and drop an item onto the canvas to add a layer."
        show={bannerVisible}
        onCloseClick={() => setBannerVisible(false)}
      />
      {isSelected && (
        <Explorer
          ref={explorerRef}
          backgroundColor="white"
          setTreeControlState={setTreeControlState}
          treeControlState={treeControlState}
          shouldRenderNode={filteredTree}
          shouldListenToBroadcastedEvents={false}
          externalDragHandling
          showQuickAccessNode={false}
          showRecycleBinNode={false}
          nodeDoubleClickHandler={doubleClickHandler}
          isSearchEnabled={true}
        />
      )}
    </div>
  );
};
